import React, { useId } from 'react'
import styled, { css } from 'styled-components'

import { PLATFORM_LOGO_MAP } from '../../model/platform'
import { PLATFORM_SQUARE_COLOR_MAP } from '../platform-colors'

import type { Platform } from '../../model'
import type { ClassNameProps } from '@root/shared/react'

type Variant = 'filled' | 'outlined'

type Props = {
  platform: Platform.Platform
  size: number
  frameSize: number
  variant?: Variant
} & ClassNameProps

export const PlatformIcon: React.FC<Props> = ({
  className,
  platform,
  size,
  frameSize,
  variant = 'filled',
}) => {
  const Logo =
    PLATFORM_LOGO_MAP[platform][variant === 'filled' ? 'Filled' : 'Outlined']

  return (
    <Root
      className={className}
      css={css`
        width: ${frameSize}px;
        height: ${frameSize}px;
      `}
    >
      <LogoWrapper>
        <Logo size={size} />
      </LogoWrapper>
      <LogoBackground platform={platform} variant={variant} />
    </Root>
  )
}

const Root = styled.div`
  border: var(--border-width, 1px) solid transparent;
  border-radius: 1px;

  position: relative;
  display: grid;

  justify-content: center;
  align-items: center;

  color: white;
`

const LogoWrapper = styled.span`
  display: grid;
  place-content: center;
  z-index: 1;

  scale: 1;
  transition: scale 0.2s cubic-bezier(0.25, 0.1, 0.25, 1);

  ${Root}:hover & {
    scale: 1.1;
  }
`

const LogoBackground = ({
  platform,
  variant = 'filled',
}: {
  platform: Platform.Platform
  variant: Variant
}) => {
  const { gradients, background, border } = PLATFORM_SQUARE_COLOR_MAP[platform]

  const tempId = useId()
  const filterId = (n: number) => `${tempId}_filter${n}`
  const clipId = `${tempId}_clip`

  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 0,
      }}
    >
      {variant === 'filled' ? (
        <g clipPath={`url(#${clipId})`}>
          <rect width="36" height="36" rx="8" fill="#1F1F1F" />
          <rect
            width="36"
            height="36"
            rx="8"
            fill={`url(#${tempId}_background)`}
            fillOpacity="0.2"
          />
          <g filter={`url(#${filterId(0)})`}>
            <path
              d="M19.8993 26.9689C15.4446 24.8588 15.5032 23.5497 16.0894 23.159C18.4339 20.8144 26.0537 15.0703 37.7764 10.8501C43.0516 14.9531 41.5863 22.8659 39.8279 32.8303C38.0695 42.7946 28.9844 36.054 28.1052 31.658C27.2259 27.262 25.4676 29.6065 19.8993 26.9689Z"
              fill={gradients[0]}
            />
          </g>
          <g filter={`url(#${filterId(1)})`}>
            <path
              d="M7.43322 29.4786C3.82936 31.3494 2.99984 30.6587 3.03556 30.0795C2.66579 27.3733 2.69443 19.5126 5.7672 9.7197C11.0873 8.30359 15.5479 13.2076 21.2083 19.3262C26.8686 25.4447 17.9199 28.0458 14.5981 26.4321C11.2762 24.8184 11.938 27.1401 7.43322 29.4786Z"
              fill={gradients[1]}
              fillOpacity="0.51"
            />
          </g>
          <g filter={`url(#${filterId(2)})`}>
            <path
              d="M1.09716 7.60363C-2.5067 9.47444 -3.33622 8.78373 -3.3005 8.20452C-3.67027 5.49828 -3.64162 -2.36242 -0.568861 -12.1553C4.75119 -13.5714 9.21184 -8.66736 14.8722 -2.54884C20.5326 3.56968 11.5839 6.17075 8.26202 4.5571C4.94016 2.94345 5.60199 5.26512 1.09716 7.60363Z"
              fill={gradients[2]}
            />
          </g>
          <g filter={`url(#${filterId(3)})`}>
            <path
              d="M16.2451 23.0621C16.9278 19.403 17.9008 19.2007 18.302 19.5569C20.4807 20.8354 26.1836 25.342 31.5659 33.1402C29.579 37.7739 23.4812 38.1828 15.8193 38.7613C8.15735 39.3398 11.3401 31.4206 14.3981 29.9528C17.4561 28.485 15.3916 27.636 16.2451 23.0621Z"
              fill={gradients[3]}
            />
          </g>
        </g>
      ) : undefined}
      <rect
        x="0.3"
        y="0.3"
        width="35.4"
        height="35.4"
        rx="7.7"
        stroke={`url(#${tempId}_border)`}
        strokeWidth="1"
        opacity={0.5}
      />
      <defs>
        <filter
          id={filterId(0)}
          x="5.80835"
          y="0.851562"
          width="45.6361"
          height="47.1484"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur stdDeviation="5" result="blur" />
        </filter>
        <filter
          id={filterId(1)}
          x="-7.12573"
          y="-0.527344"
          width="40.1674"
          height="41.2266"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur stdDeviation="5" result="blur" />
        </filter>
        <filter
          id={filterId(2)}
          x="-13.4618"
          y="-22.4023"
          width="40.1674"
          height="41.2266"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur stdDeviation="5" result="blur" />
        </filter>
        <filter
          id={filterId(3)}
          x="-8.93774"
          y="-0.566406"
          width="60.5037"
          height="59.3594"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur stdDeviation="10" result="blur" />
        </filter>
        <linearGradient
          id={`${tempId}_background`}
          x1="18"
          y1="0"
          x2="18"
          y2="36"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={background[0]} />
          <stop offset="1" stopColor={background[1]} stopOpacity="0.4" />
        </linearGradient>
        <linearGradient
          id={`${tempId}_border`}
          x1="18"
          y1="0"
          x2="18"
          y2="36"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={border[0]} />
          <stop offset="1" stopColor={border[1]} />
        </linearGradient>
        <clipPath id={clipId}>
          <rect width="36" height="36" rx="8" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}
